import type { TeamMember } from "@data/types";
import { Avatar, Badge, Box, Group, Stack, Text } from "@repo/ui/core";
import { motion } from "framer-motion";

const COMPANY_COLORS: Record<string, string> = {
  CARIAD: "violet",
  Rivian: "green",
  "American Express": "blue",
  Nike: "orange",
};

type Props = {
  member: TeamMember;
};

export function TeamCard({ member }: Props) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <Stack align="center" gap="xl">
        <Avatar
          src={member.photo}
          alt={member.name}
          size={220}
          radius="50%"
          style={{
            border: "3px solid var(--customer-accent)",
            boxShadow: "0 0 40px rgba(74, 222, 128, 0.12)",
          }}
        >
          {member.name
            .split(" ")
            .map((n) => n[0])
            .join("")}
        </Avatar>
        <Box ta="center">
          <Text size="clamp(28px, 4vw, 40px)" fw={700} lh={1.2}>
            {member.name}
          </Text>
          <Text
            size="xl"
            mt={8}
            tt="uppercase"
            fw={600}
            style={{ color: "var(--customer-accent-light)", letterSpacing: "0.05em" }}
          >
            {member.title}
          </Text>
          {member.bio && (
            <Text size="md" c="dimmed" mt="md" maw={500} mx="auto" lh={1.6}>
              {member.bio}
            </Text>
          )}
          {member.previousCompanies.length > 0 && (
            <Group gap="sm" mt="xl" justify="center" wrap="wrap">
              {member.previousCompanies.map((company) => (
                <motion.div
                  key={company}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{
                    duration: 0.3,
                    delay: 0.4 + member.previousCompanies.indexOf(company) * 0.1,
                  }}
                >
                  <Badge
                    size="lg"
                    variant="light"
                    color={COMPANY_COLORS[company] ?? "gray"}
                    style={{ letterSpacing: "0.02em" }}
                  >
                    {company}
                  </Badge>
                </motion.div>
              ))}
            </Group>
          )}
        </Box>
      </Stack>
    </motion.div>
  );
}
